/**
 * RealisationsPage — PHASE 8 APEX · B6 (pages projets publiques)
 *
 * Hub des mini-reportages : projets référence du portefeuille, servis par
 * l'endpoint public sanitisé /api/homepage-projects/public (aucune donnée
 * financière, statuts commerciaux neutralisés). Grammaire magazine :
 * grille asymétrique, légendes reportage, zéro card jumelle.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { usePublicPageData } from '../../context/PublicPageData';
import { useI18n } from '../../context/I18nContext';
import SEOHead from '../../components/SEOHead';
import { Reveal } from '../../hooks/useScrollReveal';
import EditorialImage from '../../components/public/EditorialImage';
import { optimizeImageUrl, IMG_PREVIEW } from '../../lib/imageOptimization';
import { useSiteContent, cmsVal } from '../../hooks/useSiteContent';
import { Breadcrumbs, breadcrumbJsonLd } from '../../components/editorial/Breadcrumbs';
import { localizeStatusLabel } from '../../utils/statusLabel';

const API = process.env.REACT_APP_BACKEND_URL;

export const projectSlug = (p) => (p.id || '').replace(/^hp-/, '');
export const projectCover = (p) => p.images?.[0] || p.video_poster || '';

export function useRealisations() {
  const publicData = usePublicPageData();
  const [projects, setProjects] = useState(publicData?.projects || null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/homepage-projects/public`)
      .then(res => { if (!cancelled) setProjects((res.data?.projects || []).filter(p => p.visible !== false)); })
      .catch(() => { if (!cancelled) setProjects([]); });
    return () => { cancelled = true; };
  }, []);
  return projects;
}

export default function RealisationsPage() {
  const { lang } = useI18n();
  const fr = lang === 'fr';
  const projects = useRealisations();

  // PHASE 9 P9.2 — entête + SEO pilotés par le CMS (page_id "realisations").
  // P9 C1 : la description ne raconte plus de fausses histoires (marinas,
  // Méditerranée) — typologies et territoires réels du portefeuille.
  const { content: cms } = useSiteContent('realisations');
  const v = (path, fb) => cmsVal(cms, `sections.${path}`, fb);

  const seoTitle = v('seo.title', fr
    ? 'Réalisations — Wealth Management · Global Real Assets'
    : 'Track record — Wealth Management · Global Real Assets');
  const seoDesc = v('seo.description', fr
    ? 'Références du portefeuille — hôtellerie, résidentiel et chantiers menés par la maison, présentés en reportage.'
    : 'Portfolio references — hospitality, residential and working sites carried by the house, presented as reportage.');

  /* INSTITUTIONAL DEPTH V1 (LOT 5) — cartouche de corpus calculé sur les
     données RÉELLES servies par l'API (aucun chiffre en dur). */
  const corpus = projects && projects.length > 0
    ? { count: projects.length, places: new Set(projects.map((p) => p.location).filter(Boolean)).size }
    : null;
  const crumbs = [
    { label: fr ? 'Accueil' : 'Home', to: `/${lang}` },
    { label: fr ? 'Réalisations' : 'Track record' },
  ];

  return (
    <div className="bg-ivory" data-testid="realisations-page">
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        canonical={`https://www.wealth-management-assets.com/${fr ? 'fr/realisations' : 'en/track-record'}`}
        hreflangs={{ fr: '/fr/realisations', en: '/en/track-record' }}
        ogImage="https://www.wealth-management-assets.com/images/brand/chantier_grues_crepuscule.webp"
        jsonLd={breadcrumbJsonLd(crumbs)}
      />

      <header className="container-editorial pt-20 pb-16 lg:pt-28 lg:pb-20">
        <Breadcrumbs items={crumbs} className="mb-10" testid="realisations-breadcrumbs" ariaLabel={fr ? 'Fil d\u2019ariane' : 'Breadcrumb'} />
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-block h-px w-6 bg-gold-dark" aria-hidden />
            <span className="text-[11px] tracking-[0.16em] uppercase font-medium text-gold-dark">
              {v('header.overline', fr ? 'Références de la maison' : 'House references')}
            </span>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="font-heading font-medium text-ink text-[2.5rem] sm:text-[3.2rem] lg:text-[3.8rem] leading-[1.05] tracking-[-0.015em] max-w-3xl text-balance" data-testid="realisations-title">
            {v('header.title_1', fr ? 'Ce que nous avons' : 'What we have')}{' '}
            <span className="italic text-gold-dark">{v('header.title_em', fr ? 'construit.' : 'built.')}</span>
          </h1>
        </Reveal>
        <Reveal delay={180}>
          <p className="text-graphite text-base lg:text-lg leading-relaxed max-w-2xl mt-6">
            {v('header.lede', fr
              ? 'Une sélection de projets référence, présentés comme nous les avons vécus : des lieux, des matériaux, des chantiers. Les opérations en cours restent réservées à l\u2019espace privé.'
              : 'A selection of reference projects, presented as we lived them: places, materials, working sites. Current operations remain reserved for the private space.')}
          </p>
        </Reveal>
        {corpus && (
          <Reveal delay={260}>
            <p className="flex flex-wrap items-baseline gap-x-8 gap-y-2 mt-8 pt-6 border-t border-stone/70 max-w-2xl" data-testid="realisations-corpus">
              <span className="text-[12px] text-graphite/80">
                <span className="font-heading text-charcoal text-xl lining-nums mr-2">{corpus.count}</span>
                {fr ? 'références documentées' : 'documented references'}
              </span>
              <span className="text-[12px] text-graphite/80">
                <span className="font-heading text-charcoal text-xl lining-nums mr-2">{corpus.places}</span>
                {fr ? 'territoires' : 'territories'}
              </span>
              <span className="text-[12px] text-graphite/80">
                {fr ? 'Chaque référence se lit en étude de cas.' : 'Each reference reads as a case study.'}
              </span>
            </p>
          </Reveal>
        )}
      </header>

      <main className="container-editorial pb-28 lg:pb-36">
        {projects === null ? (
          <p className="text-graphite text-sm py-16" data-testid="realisations-loading">
            {fr ? 'Chargement…' : 'Loading…'}
          </p>
        ) : projects.length === 0 ? (
          <p className="text-graphite text-base py-16" data-testid="realisations-empty">
            {fr ? 'Les projets référence seront présentés ici prochainement.' : 'Reference projects will be presented here soon.'}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-10 lg:gap-x-14 gap-y-16">
            {projects.map((p, i) => {
              const wide = i % 3 === 0; // rythme éditorial : 1 large, 2 moyens
              const cover = projectCover(p);
              return (
                <Reveal
                  key={p.id}
                  delay={(i % 2) * 120}
                  className={wide ? 'md:col-span-7' : 'md:col-span-5'}
                >
                  <Link
                    to={`/${lang}/${fr ? 'realisations' : 'track-record'}/${projectSlug(p)}`}
                    className={`group block ${!wide && i % 3 === 2 ? 'md:mt-16' : ''}`}
                    data-testid={`realisation-card-${projectSlug(p)}`}
                  >
                    {cover ? (
                      <EditorialImage src={optimizeImageUrl(cover, IMG_PREVIEW)} alt={p.name} ratio={wide ? '3/2' : '4/3'} />
                    ) : (
                      <div className="bg-navy-deep" style={{ aspectRatio: wide ? '3/2' : '4/3' }} />
                    )}
                    <div className="mt-6 flex items-baseline gap-3">
                      <span className="font-heading italic text-gold-dark text-lg leading-none shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <p className="text-[11px] tracking-[0.16em] uppercase font-medium text-gold-dark mb-2">
                          {p.location}
                        </p>
                        <h2 className="font-heading font-medium text-ink text-[1.5rem] lg:text-[1.8rem] leading-[1.15] mb-2 transition-colors duration-200 group-hover:text-gold-dark text-balance">
                          {p.name}
                        </h2>
                        <p className="text-graphite text-sm leading-relaxed line-clamp-2">{p.description}</p>
                        <p className="text-[12px] text-graphite/80 mt-3">
                          {p.asset_class}
                          {p.status_label && !/référence|reference/i.test(p.status_label) && (
                            <span className="text-gold-dark" data-testid={`realisation-status-${projectSlug(p)}`}>
                              {' · '}{localizeStatusLabel(p.status_label, lang)}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}

        <p className="text-xs text-graphite/80 leading-relaxed mt-20 max-w-3xl border-t border-stone pt-6" data-testid="realisations-disclaimer">
          {fr
            ? 'Les projets présentés sont des références passées ou des illustrations du savoir-faire de la maison. Ils ne constituent ni une offre, ni une sollicitation, ni une promesse de performance. Les performances passées ne préjugent pas des performances futures.'
            : 'The projects shown are past references or illustrations of the house\u2019s expertise. They constitute neither an offer, nor a solicitation, nor a promise of performance. Past performance is not indicative of future results.'}
        </p>
      </main>
    </div>
  );
}
